import { Field, Label, Link, Radio, RadioGroup, Title3, makeStyles, tokens } from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../AppState';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AttachmentProvider } from '../../contexts/AttachmentContext';
import { useChat } from '../../libs/hooks/useChat';
import { isAllowedByPermissionsPolicy } from '../../libs/utils/permissionsPolicy';
import { resolvePrivacyPolicyUrl } from '../../libs/utils/privacyPolicy';
import { ChatRoomHeader } from '../chat/ChatRoomHeader';
import { ChatRoomInlineHeader } from '../chat/ChatRoomInlineHeader';
import { ChatSidebar } from '../chat/ChatSidebar';
import { ResponseStyle, isResponseStyle } from '../../libs/models/UserPreferences';
import { useUserPreferences } from '../../libs/hooks/useUserPreferences';
import { SpirisConnectionSection } from './SpirisConnectionSection';
import { UserMemorySection } from './UserMemorySection';
import { VoiceSettingsSection } from './VoiceSettingsSection';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'var(--app-shadow-card)',
    },
    mainContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'auto',
        paddingLeft: '0',
        paddingRight: '0',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalL,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
    },
    headerEmbedded: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        padding: tokens.spacingVerticalS,
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
    },
    pageTitle: {
        fontFamily: tokens.fontFamilyBase,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: '20px',
        lineHeight: '100%',
        marginTop: tokens.spacingVerticalS,
        paddingBottom: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    section: {
        padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    privacyField: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
    },
    // Layout only — Fluent UI's Link owns the link color and hover underline.
    privacyLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        width: 'fit-content',
    },
});

export const SettingsView: React.FC = () => {
    const classes = useClasses();
    const { embeddedMode } = useAppSelector((state: RootState) => state.app);
    const responseStyle = useAppSelector((state: RootState) => state.app.userPreferences.responseStyle);
    // The response-style section is flag-gated on ResponseStylePreferenceEnabled.
    const responseStylePreferenceEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.ResponseStylePreferenceEnabled,
    );
    const userMemoryEnabled = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.UserMemoryEnabled,
    );
    const spirisEnabled = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags.UseSpirisMcp);
    // Where the voice buttons are shown (ChatInput): a setting for a conversation the page cannot start would only confuse.
    const voiceModeEnabled = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags.VoiceModeEnabled);
    const showVoiceSettings = voiceModeEnabled && isAllowedByPermissionsPolicy('microphone');
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const chat = useChat();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { updateResponseStyle } = useUserPreferences();

    // Unwrapped on purpose (matches HistoryPage/LicencesView): reached only via the setAppState
    // interceptor below, which the header's already-guarded onNewChat triggers — wrapping again would
    // double-guard (second warning dialog + an orphan chat if the customer cancels it). navigate('/')
    // is required or a new chat is created in Redux but the router stays on /settings.
    const handleNewChat = async () => {
        try {
            await chat.createChat(hostContext);
            await navigate('/');
        } catch (error) {
            console.error('Failed to create new chat:', error);
        }
    };

    const setAppState: React.Dispatch<React.SetStateAction<AppState>> = (state) => {
        if (state === AppState.SettingUserInfo) {
            void handleNewChat();
        }
    };

    const onResponseStyleChange = (_: unknown, data: { value: string }) => {
        const next = data.value;
        if (isResponseStyle(next) && next !== responseStyle) {
            updateResponseStyle(next).catch(() => {
                // Errors are logged inside the hook.
            });
        }
    };

    return (
        <AttachmentProvider>
            <div className={classes.root}>
                {!embeddedMode && <ChatRoomHeader setAppState={setAppState} />}
                <ChatRoomInlineHeader setAppState={setAppState} isVisible={embeddedMode} />
                <div className={classes.mainContainer}>
                    <ChatSidebar />
                    <div className={classes.content}>
                        <div className={embeddedMode ? classes.headerEmbedded : classes.header}>
                            <Title3 className={classes.pageTitle}>{t('settings.title')}</Title3>
                        </div>
                        {responseStylePreferenceEnabled && (
                            <div className={classes.section}>
                                <Field label={t('userSettingsMenu.responseStyle.title')}>
                                    <RadioGroup
                                        value={responseStyle}
                                        onChange={onResponseStyleChange}
                                        data-testid="responseStyleRadioGroup"
                                    >
                                        <Radio
                                            value={ResponseStyle.Brief}
                                            label={t('userSettingsMenu.responseStyle.brief')}
                                            data-testid="responseStyleBriefRadio"
                                        />
                                        <Radio
                                            value={ResponseStyle.Detailed}
                                            label={t('userSettingsMenu.responseStyle.detailed')}
                                            data-testid="responseStyleDetailedRadio"
                                        />
                                    </RadioGroup>
                                </Field>
                            </div>
                        )}
                        <div className={classes.section}>
                            <div className={classes.privacyField}>
                                <Label>{t('settings.privacyStatement.sectionTitle')}</Label>
                                <Link
                                    href={resolvePrivacyPolicyUrl(t('settings.privacyStatement.url'), hostContext)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classes.privacyLink}
                                    data-testid="privacyStatementLink"
                                >
                                    {t('settings.privacyStatement.link')}
                                    <OpenRegular />
                                </Link>
                            </div>
                        </div>
                        {showVoiceSettings && (
                            <div className={classes.section}>
                                <VoiceSettingsSection />
                            </div>
                        )}
                        {userMemoryEnabled && (
                            <div className={classes.section}>
                                <UserMemorySection />
                            </div>
                        )}
                        {spirisEnabled && (
                            <div className={classes.section}>
                                <SpirisConnectionSection />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AttachmentProvider>
    );
};
